import { useMemo } from 'react';
import { format, isAfter, parseISO, startOfDay } from 'date-fns';
import { getApiErrorMessage } from '@/api/helpers';
import { SlideOver } from '@/components/calendar/panels/SlideOver';
import { useHrmsHolidaysQuery } from '@/hooks/hrms';
import { useOrgHolidaysQuery } from '@/hooks/calendar/useOrgQueries';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type DisplayHoliday = {
  key: string;
  name: string;
  date: Date;
  isUpcoming: boolean;
  source: 'hrms' | 'calendar';
};

const useCalendarOrgHolidays = String(import.meta.env.VITE_USE_CALENDAR_ORG_HOLIDAYS || '').trim() === '1';

export function OrgHolidaysPanel({ isOpen, onClose }: Props) {
  const hrmsHolidaysQuery = useHrmsHolidaysQuery(isOpen);
  const calendarHolidaysQuery = useOrgHolidaysQuery(isOpen && useCalendarOrgHolidays);

  const isPending = hrmsHolidaysQuery.isPending || (useCalendarOrgHolidays && calendarHolidaysQuery.isPending);
  const errorMessage = useMemo(() => {
    const err = hrmsHolidaysQuery.error ?? calendarHolidaysQuery.error;
    return err ? getApiErrorMessage(err, 'Could not load holidays') : null;
  }, [calendarHolidaysQuery.error, hrmsHolidaysQuery.error]);

  const grouped = useMemo(() => groupHolidaysByYear(buildDisplayList(hrmsHolidaysQuery.data, calendarHolidaysQuery.data)), [
    calendarHolidaysQuery.data,
    hrmsHolidaysQuery.data,
  ]);

  if (!isOpen) return null;

  return (
    <SlideOver onClose={onClose} widthClass="max-w-xl" labelledBy="org-holidays-panel-title">
      {(close) => (
        <>
        <header className="sticky top-0 z-10 border-b border-slate-100 bg-white px-8 py-5 dark:border-white/5 dark:bg-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 id="org-holidays-panel-title" className="text-xl font-bold tracking-tight text-[#0f172b] dark:text-white">Holidays</h3>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Organization holiday calendar
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              aria-label="Close holidays panel"
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6 custom-scrollbar">
          {errorMessage ? (
            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-sm dark:border-white/5 dark:bg-card" role="alert">
              <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-500/10 dark:text-rose-400">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </span>
              <p className="font-medium text-slate-600 dark:text-slate-300">{errorMessage}</p>
            </div>
          ) : null}

          {isPending ? (
            <HolidayListSkeleton />
          ) : grouped.length === 0 ? (
            <EmptyState />
          ) : (
            grouped.map((bucket) => (
              <section key={bucket.year} className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{bucket.year}</h4>
                  <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[11px] font-semibold text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]">
                    {bucket.items.length} {bucket.items.length === 1 ? 'holiday' : 'holidays'}
                  </span>
                </div>
                <ul className="space-y-2">
                  {bucket.items.map((h) => (
                    <li
                      key={h.key}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <DateBlock date={h.date} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#0f172b] dark:text-white">{h.name}</p>
                          <p className="truncate text-xs text-slate-500 dark:text-slate-400">{format(h.date, 'EEEE, MMM d, yyyy')}</p>
                        </div>
                      </div>
                      <StatusChip upcoming={h.isUpcoming} />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
        </>
      )}
    </SlideOver>
  );
}

function buildDisplayList(
  hrms: Array<{ id: string; name?: string; date?: string; isActive?: boolean }> | undefined,
  calendar: Array<{ id: string; name: string; startsOn: string }> | undefined
): DisplayHoliday[] {
  const today = startOfDay(new Date());
  const seen = new Map<string, DisplayHoliday>();

  for (const h of hrms ?? []) {
    if (!h?.date) continue;
    if (h.isActive === false) continue;
    const date = parseISO(h.date);
    if (Number.isNaN(date.getTime())) continue;
    const name = (h.name ?? 'Holiday').trim();
    const dedupeKey = `${format(date, 'yyyy-MM-dd')}|${name.toLowerCase()}`;
    seen.set(dedupeKey, {
      key: `hrms-${h.id}`,
      name,
      date,
      isUpcoming: !isAfter(today, date),
      source: 'hrms',
    });
  }

  for (const h of calendar ?? []) {
    if (!h?.startsOn) continue;
    const date = parseISO(h.startsOn);
    if (Number.isNaN(date.getTime())) continue;
    const name = (h.name ?? 'Holiday').trim();
    const dedupeKey = `${format(date, 'yyyy-MM-dd')}|${name.toLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.set(dedupeKey, {
      key: `cal-${h.id}`,
      name,
      date,
      isUpcoming: !isAfter(today, date),
      source: 'calendar',
    });
  }

  return Array.from(seen.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
}

function groupHolidaysByYear(list: DisplayHoliday[]): Array<{ year: number; items: DisplayHoliday[] }> {
  const map = new Map<number, DisplayHoliday[]>();
  for (const h of list) {
    const year = h.date.getFullYear();
    const arr = map.get(year) ?? [];
    arr.push(h);
    map.set(year, arr);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, items]) => ({ year, items }));
}

function DateBlock({ date }: { date: Date }) {
  return (
    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-[#EEF2FF] text-center dark:bg-[#1C1F3F]">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#5B47E8] dark:text-[#8B7CFF]">{format(date, 'MMM')}</span>
      <span className="text-base font-bold leading-none text-[#0f172b] dark:text-white">{format(date, 'd')}</span>
    </div>
  );
}

function StatusChip({ upcoming }: { upcoming: boolean }) {
  return (
    <span
      className={
        upcoming
          ? 'shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
          : 'shrink-0 rounded-full bg-[#F3F4F8] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-white/5 dark:text-slate-400'
      }
    >
      {upcoming ? 'Upcoming' : 'Past'}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center rounded-3xl border border-dashed border-[#5B6CF6]/30 bg-white px-6 py-12 text-center dark:border-[#8B7CFF]/30 dark:bg-card">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l2.39 4.84 5.34.78-3.86 3.77.91 5.32L12 14.27 7.22 16.7l.91-5.32L4.27 7.62l5.34-.78L12 2z" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-[#0f172b] dark:text-white">No holidays configured</p>
      <p className="mt-1 max-w-xs text-xs text-slate-500 dark:text-slate-400">
        Holidays are managed in HRMS. Once added there, they will appear here automatically.
      </p>
    </div>
  );
}

function HolidayListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 dark:border-white/5 dark:bg-card">
          <div className="h-12 w-12 animate-pulse rounded-2xl bg-[#F3F4F8] dark:bg-white/5" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/2 animate-pulse rounded-full bg-[#F3F4F8] dark:bg-white/5" />
            <div className="h-2 w-1/3 animate-pulse rounded-full bg-[#F3F4F8] dark:bg-white/5" />
          </div>
          <div className="h-5 w-16 animate-pulse rounded-full bg-[#F3F4F8] dark:bg-white/5" />
        </div>
      ))}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
